import type { PropsWithChildren, ReactElement, SyntheticEvent } from "react";
import { useCallback, useMemo } from "react";

import type { IPopoverOptions } from "@similarweb/ui-components";
import { Popover, PopoverTrigger, Tooltip } from "@similarweb/ui-components";

import SignalSelectionTooltipList from "app/pages/sales-intelligence/sub-modules/signals/components/shared/SignalSelectionTooltipList/SignalSelectionTooltipList";
import {
    StyledSignalSelectionTooltipInlineTrigger,
    StyledSignalSelectionTooltipTrigger,
} from "app/pages/sales-intelligence/sub-modules/signals/components/shared/SignalSelectionTooltipTrigger/styles";

import {
    StyledSignalSelectionPopoverContent,
    StyledSignalSelectionTooltipSurface,
} from "./signalSelectionTooltipSurfaceStyles";

type Interaction = "popover" | "tooltip";

type Props = PropsWithChildren<{
    labels: readonly string[];
    placement?: IPopoverOptions["placement"];
    enabled?: boolean;
    triggerFlexGrow?: boolean;
    /** Use inline layout so overflow suffixes (e.g. `+N`) wrap with label text. */
    inlineTextTrigger?: boolean;
    triggerDataAutomation?: string;
    /**
     * `tooltip` — hover inside parent Dropdown/Menu (Popover hover closes the parent).
     * `popover` — filter button label when the parent dropdown is closed.
     */
    interaction?: Interaction;
}>;

const SIGNAL_SELECTION_TOOLTIP_CLOSE_DELAY_MS = 100;

const SignalSelectionTooltip = ({
    children,
    labels,
    placement = "top",
    enabled,
    triggerFlexGrow = false,
    inlineTextTrigger = false,
    triggerDataAutomation,
    interaction = "popover",
}: Props): ReactElement => {
    const showTooltip = enabled ?? labels.length > 1;

    const stopWheelPropagation = useCallback((event: SyntheticEvent) => {
        event.stopPropagation();
    }, []);

    const stopPointerPropagation = useCallback((event: SyntheticEvent) => {
        event.stopPropagation();
    }, []);

    const listContent = useMemo(
        () => (
            <StyledSignalSelectionTooltipSurface data-automation="signal-selection-tooltip-content">
                <SignalSelectionTooltipList labels={labels} />
            </StyledSignalSelectionTooltipSurface>
        ),
        [labels],
    );

    if (!showTooltip) {
        return <>{children}</>;
    }

    const trigger = inlineTextTrigger ? (
        <StyledSignalSelectionTooltipInlineTrigger
            data-automation={triggerDataAutomation}
            onPointerDown={stopPointerPropagation}
            onClick={stopPointerPropagation}
        >
            {children}
        </StyledSignalSelectionTooltipInlineTrigger>
    ) : (
        <StyledSignalSelectionTooltipTrigger
            $flexGrow={triggerFlexGrow}
            data-automation={triggerDataAutomation}
            onPointerDown={stopPointerPropagation}
            onClick={stopPointerPropagation}
        >
            {children}
        </StyledSignalSelectionTooltipTrigger>
    );

    if (interaction === "tooltip") {
        return (
            <Tooltip
                enabled
                placement={placement}
                delay={{ close: SIGNAL_SELECTION_TOOLTIP_CLOSE_DELAY_MS }}
                content={listContent}
            >
                {trigger}
            </Tooltip>
        );
    }

    return (
        <Popover
            offset={6}
            placement={placement}
            withArrow
            isHover
            delay={{ close: SIGNAL_SELECTION_TOOLTIP_CLOSE_DELAY_MS }}
        >
            <PopoverTrigger asChild>{trigger}</PopoverTrigger>

            <StyledSignalSelectionPopoverContent
                data-automation="signal-selection-popover-content"
                onWheel={stopWheelPropagation}
            >
                <SignalSelectionTooltipList labels={labels} />
            </StyledSignalSelectionPopoverContent>
        </Popover>
    );
};

export default SignalSelectionTooltip;
